import React, { useState, useContext, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ConfirmModal } from '@mummssoftware/common-ui';
import { getCurrentMilestone } from '@mummssoftware/utils/hope';
import styled from 'styled-components';
import {
  HopeGlobalContext,
  GlobalContext,
  SET_IS_HOPE_VISIT,
  UPDATE_SIA_DATA,
  SET_SIA_DISPLAY,
  SET_HOPE_VISIT_PROMPT_DISPLAY,
} from '@mummssoftware/global-context';
import Menu from '@material-ui/core/Menu';
import { useTheme } from '@material-ui/core/styles';
import { VisitContext } from '../Context/contexts/VisitContext';
import { SIAContent } from './SIAContent';
import {
  HOPE_VISIT_ICON_ID,
  milestoneQuestionNumber,
  SFVQuestionNumber,
} from './hopeConstants';
import defaults from '../../translations/en.json';

const hopeMenuProps = {
  disableAutoFocus: true,
  disableRestoreFocus: true,
  transitionDuration: 0,
  keepMounted: true,
};

const IconDiv = styled.div<{
  color?: string;
  $visible?: boolean;
  $disabled?: boolean;
}>`
  ${(props: any) => `background-color: ${props.color || '#880808'};`}
  ${(props: any) => `color: ${props.theme.palette.common.white};`}
  width: 4em;
  height: 20px;
  cursor: ${(props: any) => (props.$disabled ? 'not-allowed' : 'pointer')};
  padding: 1px;
  border-radius: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: center;
  font-family: 'Muli', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
  opacity: ${(props: any) => (props.$disabled ? 0.6 : 1)};
  visibility: ${(props: any) => (props.$visible ? 'visible' : 'hidden')};
`;

type SIAModalProps = {
  milestonesAvailable: boolean;
  /** Grey out HOPE UI when another user has an in-progress HOPE visit (SIAModal stays mounted). */
  hopeDisabled?: boolean;
  /** Reports the HOPE chip element (null on unmount) so VisitPromptStack can anchor to it. */
  onHopeIconRef?: (node: HTMLElement | null) => void;
};

export const SIAModal = ({
  milestonesAvailable,
  hopeDisabled = false,
  onHopeIconRef,
}: SIAModalProps) => {
  const {
    dispatch: dispatchHopeGlobal,
    isHopeVisit,
    SIAData,
    availableHOPEActions,
    displaySIA,
    hopeVisitAttributesSaved,
    getAllUsersHopeMilestones,
    saveHopePatientSIAForVisit,
  } = useContext(HopeGlobalContext);
  const { appVisit } = useContext(GlobalContext);
  const { persistHopeVisitAttributes } = useContext(VisitContext);

  const { palette } = useTheme();

  const [iconEl, setIconEl] = useState<HTMLElement | null>(null);

  const setHopeIconRef = useCallback(
    (node: HTMLElement | null) => {
      setIconEl(node);
      if (onHopeIconRef) {
        onHopeIconRef(node);
      }
    },
    [onHopeIconRef],
  );

  const [stateSIA, setStateSIA] = useState<Record<string, unknown>>({
    ...SIAData,
  });
  const currentMilestone = getCurrentMilestone(availableHOPEActions);
  const isSFV = currentMilestone?.includes('-SFV');
  const period = isSFV ? defaults.SFVPeriod : defaults.MilestonePeriod;
  const SIAMessage = `${period}${defaults.SIAStatement}`;
  const questionNumber = isSFV ? SFVQuestionNumber : milestoneQuestionNumber;

  const updateSIA = (event: any, symptomDtoProp: string) => {
    const newState = { ...stateSIA };
    newState[symptomDtoProp] = event.target.value;
    setStateSIA(newState);
  };

  useEffect(() => {
    if (!Object.keys(SIAData).length) {
      return;
    }
    if (displaySIA) {
      // Prefill from server while modal is open only if the form is still empty
      setStateSIA((prev) => (Object.keys(prev).length ? prev : { ...SIAData }));
      return;
    }
    setStateSIA({ ...SIAData });
  }, [SIAData, displaySIA]);

  const getIconColor = () => {
    if (hopeDisabled) {
      return palette.text.secondary;
    }
    if (isHopeVisit === null) {
      return palette.text.secondary;
    }
    if (isHopeVisit) {
      return palette.success.main;
    }
    return palette.text.secondary;
  };

  return (
    <>
      <IconDiv
        ref={setHopeIconRef}
        id={HOPE_VISIT_ICON_ID}
        $disabled={hopeDisabled}
        onClick={async () => {
          if (hopeDisabled) {
            return;
          }
          if (isHopeVisit) {
            if (hopeVisitAttributesSaved) {
              dispatchHopeGlobal({ type: SET_SIA_DISPLAY, payload: true });
            } else {
              await persistHopeVisitAttributes({
                isHopeVisit: true,
                stateSIA,
                openSIAOnSave: true,
              });
            }
            return;
          }
          await getAllUsersHopeMilestones();
          dispatchHopeGlobal({
            type: SET_HOPE_VISIT_PROMPT_DISPLAY,
            payload: true,
          });
        }}
        color={getIconColor()}
        $visible={milestonesAvailable}
      >
        HOPE
      </IconDiv>
      <Menu
        anchorEl={iconEl}
        open={displaySIA && !!iconEl && !hopeDisabled}
        {...hopeMenuProps}
      >
        <ConfirmModal
          onOKClick={async () => {
            ReactDOM.unstable_batchedUpdates(() => {
              dispatchHopeGlobal({ type: SET_SIA_DISPLAY, payload: false });
              dispatchHopeGlobal({
                type: UPDATE_SIA_DATA,
                payload: stateSIA,
              });
            });
            const saved = await persistHopeVisitAttributes({
              isHopeVisit: true,
              stateSIA,
            });
            if (saved && appVisit?.id) {
              await saveHopePatientSIAForVisit(stateSIA, appVisit.id);
            }
          }}
          onCancelClick={() => {
            ReactDOM.unstable_batchedUpdates(() => {
              dispatchHopeGlobal({ type: SET_SIA_DISPLAY, payload: false });
              dispatchHopeGlobal({ type: SET_IS_HOPE_VISIT, payload: false });
              dispatchHopeGlobal({ type: UPDATE_SIA_DATA, payload: {} });
            });
            setStateSIA({});
            persistHopeVisitAttributes({ isHopeVisit: false });
          }}
          okText={defaults.ok}
          noText={defaults.removeHope}
          modalText=""
          content={
            <SIAContent
              stateSIA={stateSIA}
              updateSIA={updateSIA}
              currentMilestone={currentMilestone}
              isSFV={isSFV}
              SIAMessage={SIAMessage}
              questionNumber={questionNumber}
            />
          }
        />
      </Menu>
    </>
  );
};
